import React, { useState, useEffect } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { Link, graphql } from 'gatsby';
import styled from 'styled-components';

import Layout from '~/components/Layout';
import Seo from '~/components/SEO';
import CompanyBottomNav from '~/components/CompanyBottomNav';
import ContactForm from '~/components/Forms/Contact/ContactForm';
import TextCard from '~/components/Dynamic/TextCard';
import Image from '~/components/Dynamic/Image';
import useStoryblok from '~/utils/storyblok-service';

import BlackArrow from '~/icons/black-arrow.svg';

const ContactHeroCard = styled.div`
  &:hover {
    .gradient-hover-show {
      opacity: 100;
    }
  }
`;

function Contact({ data, location, pageContext }) {
  const story = useStoryblok(data.storyblokEntry, location);
  const content = story.content;
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { hero, buttons, formHeading, pressMedia, socials, twoColumns, boxLinksHeading, socialHeading } = content;

  // Scroll to correct part of page if form submitted
  useEffect(() => {
    if (isSubmitted === true && typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        const anchor = document.querySelector('#sales');
        if (!anchor) return;
        const offset = anchor.getBoundingClientRect().top + window.scrollY - 80;
        window.scroll({ top: offset, left: 0, behavior: 'smooth' });
      });
    }
  }, [isSubmitted]);

  return (
    <Layout hideFooterCta locale={pageContext.locale}>
      <Seo {...content.seo} lang={story.lang} pageContext={pageContext} />

      <section className="my-20 md:my-28">
        <div className="container">
          <div className="flex flex-col items-center text-center">
            <div className="max-w-[130px]">
              <Image blok={hero[0]?.icon} className="w-[90px] md:w-[130px]" />
            </div>
            <div className="text-sh mt-7">{hero[0]?.subHeading}</div>
            <h1 className="mt-2 text-h1">{hero[0]?.heading}</h1>
          </div>

          <div className="mt-24">
            <div className="flex flex-wrap -mt-6 -ml-6">
              {twoColumns[0].content.map(({ subHeading, heading, content, button }, i) => (
                <ContactHeroCard key={`contact-hero${i}`} className="flex flex-col w-full pl-6 mt-6 md:w-6/12">
                  <Link
                    to={button[0]?.button?.url}
                    className="relative flex flex-col flex-auto p-12 text-center transition-colors border border-black cursor-default md:text-left md:p-14 hover:border-white"
                  >
                    <div className="absolute inset-0 z-0 transition-opacity ease-in-out opacity-0 pointer-events-none gradient-hover-show duration-250">
                      <StaticImage
                        src={`../assets/images/hero-gradient.jpg`}
                        loading="eager"
                        alt="hero background"
                        className="w-full h-full"
                        objectFit="cover"
                        placeholder="blurred"
                      />
                    </div>
                    <div className="relative flex flex-col items-start justify-between flex-auto">
                      <div>
                        <div className="text-sh">{subHeading}</div>
                        <h2 className="max-w-xs mt-3 text-h2">{heading}</h2>
                        <div className="mt-5 prose md:text-20px" dangerouslySetInnerHTML={{ __html: content }} />
                      </div>
                      <div className="w-full mt-11 md:w-auto">
                        <a
                          href={button[0]?.button?.url}
                          className={`c-btn c-btn--primary cursor-pointer justify-center md:justify-start`}
                        >
                          {button[0].title}
                        </a>
                      </div>
                    </div>
                  </Link>
                </ContactHeroCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="my-20 md:my-28">
        <div className="container">
          <div className="flex flex-wrap items-center -ml-36">
            <div className="w-full md:w-6/12 pl-36">
              <Image blok={content?.baseImage} />
            </div>
            <div className="w-full text-center md:w-6/12 pl-36 md:text-left">
              <div>
                <h2 className="mt-12 md:mt-12 md:max-w-md text-h2">{content?.base[0]?.heading}</h2>
                <div className="mt-8 md:mt-16">
                  <h4 className="font-bold uppercase text-16px ">{content?.base[0]?.addressLine1}</h4>
                  <div className="mb-8 italic font-medium md:mb-14">
                    <p>{content?.base[0]?.addressLine2}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="my-20 md:my-28">
        <div className="container">
          <div className="flex-col items-center text-center md:flex md:text-left">
            <h2 className="text-h2">{boxLinksHeading}</h2>
            <div className="w-full max-w-xl mx-auto">
              <div className="flex flex-wrap justify-center mt-12 -ml-6">
                <div className="w-full pl-6 md:w-1/2">
                  <Link
                    to={pressMedia[0]?.button?.url}
                    className="flex items-center justify-between bg-black c-btn c-btn--white-no-icon c-btn--outline c-btn--outline--black-shadow py-9"
                  >
                    <div>{pressMedia[0]?.title}</div>
                    <img className={`ml-20`} src={BlackArrow} alt="" />
                  </Link>
                </div>
                <div className="w-full pl-6 mt-6 md:w-1/2 md:mt-0 flex flex-col">
                  <div className="flex items-center justify-between bg-black c-btn c-btn--white-no-icon c-btn--outline c-btn--outline--black-shadow no-hover py-9 flex-auto">
                    <div>{socialHeading}</div>
                    <div className="flex items-center -ml-2">
                      {socials?.map((socialLink, i) => (
                        <a
                          key={`socialLink${i}`}
                          href={socialLink?.url}
                          target="_blank"
                          className="pl-2 transition-opacity duration-200 hover:opacity-50"
                        >
                          <img src={socialLink?.icon?.filename} />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="sales" className="my-20 bg-light-grey md:my-28">
        <div className="container py-20 md:py-28">
          <div className="mx-auto max-w-36 ">
            <div className="flex flex-col">
              {!isSubmitted ? (
                <>
                  <h2 className="text-h2">{formHeading}</h2>
                  <ContactForm setIsSubmitted={setIsSubmitted} />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center flex-auto text-center">
                  <div className="max-w-lg mx-auto">
                    {content?.formConfirmation?.length > 0 && (
                      <TextCard
                        blok={{
                          ...content?.formConfirmation[0],
                          iconClass: 'max-w-[40px] mx-auto',
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <CompanyBottomNav items={buttons} />
    </Layout>
  );
}

export default Contact;

export const query = graphql`
  query ContactQuery($id: String!) {
    storyblokEntry(id: { eq: $id }) {
      id
      name
      content
      lang
      slug
      uuid
      full_slug
    }
  }
`;
